import { useCallback, useContext, useEffect, useState } from 'react';
import history from '@history';
import * as yup from 'yup';
import _ from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import Switch from '@mui/material/Switch';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { regexEmail, regexKeyName, regexURL } from 'src/app/utils/validations';
import {BasicSelect, ButtonWithLoader, FormCardLayout} from '@main/components';
import { useRequest } from '@main/hooks';
import { availablePleskConnections, createMainDomain } from 'src/app/api/setup';
import { convertObjToPickerMono, isEmptyString } from 'src/app/utils/conversors';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/core/messageSlice';
import HelpIcon from '@mui/icons-material/Help';
import { showConnectionPlesk } from 'app/store/modals';
import { Grid } from '@mui/material';
import { StepperContext } from '../Context/SetupContext';
import { PhoneCodeSelect } from '../../../components';
// import { generateHexColor } from 'src/app/utils/generators';
// import { useWithApiValidation } from '@main/hooks/useWithApiValidation';
// import { validateEmail, validatePhone } from 'src/app/api/validations';

const pleskSchema = yup.object().shape({
  name: yup
    .string()
    .required('El nombre no puede quedar vacío')
    .min(3, 'El nombre debe contener al menos 3 letras'),
  // .matches(regexName, 'Solo letras y espacios entre nombres'),
  email: yup
    .string()
    .matches(regexEmail, 'Debes ingresar un correo válido')
    .required('El correo no puede quedar vacío'),
  phoneCode: yup.string().min(1, 'El lada debe tener al menos 1 dígito'),
  phone: yup.string().notRequired().min(10, 'Verifique que el número tenga 10 cifras al menos'),

  // ----- Plesk ------
  domain: yup
    .string()
    .nullable()
    .matches(regexURL, { message: 'No es una dirección válida', excludeEmptyString: true }),
  isManual: yup.boolean(),
  connection: yup
    .string()
    .nullable()
    .when('isManual', {
      is: (valIsManual) => !valIsManual,
      then: (schem) => yup.string().required().matches(regexKeyName, 'No debe contener espacios'),
    }),
  ip: yup
    .string()
    .nullable()
    .when('isManual', {
      is: (valIsManual) => valIsManual,
      then: (schem) => yup.string().required().matches(regexURL, 'No es una dirección válida'),
    }),
  owner: yup
    .string()
    .nullable()
    .when('isManual', {
      is: (valIsManual) => valIsManual,
      then: (schem) => yup.string().required().matches(regexKeyName, 'No debe contener espacios'),
    }),
  servicePlan: yup
    .string()
    .nullable()
    .when('isManual', {
      is: (valIsManual) => valIsManual,
      then: (schem) => yup.string().required().matches(regexKeyName, 'No debe contener espacios'),
    }),
  baseUri: yup
    .string()
    .nullable()
    .when('isManual', {
      is: (valIsManual) => valIsManual,
      then: (schem) => yup.string().required().matches(regexURL, 'No es una dirección válida'),
    }),

  // ---- Redis ----
  redisUser: yup
    .string()
    .nullable()
    .when('isManual', {
      is: (valIsManual) => valIsManual,
      then: (schem) => yup.string().required().matches(regexKeyName, 'No debe contener espacios'),
    }),
  redisPassword: yup
    .string()
    .nullable()
    .when('isManual', {
      is: (valIsManual) => valIsManual,
      then: (schem) => yup.string().required().matches(regexKeyName, 'No debe contener espacios'),
    }),
  authType: yup.boolean(),
  key: yup
    .string()
    .nullable()
    .when(['authType', 'isManual'], {
      is: (valAuthType, valIsManual) => !valAuthType && valIsManual,
      then: (schem) => yup.string().required().matches(regexKeyName, 'No es una key válida'),
    }),
  user: yup
    .string()
    .nullable()
    .when(['authType', 'isManual'], {
      is: (valAuthType, valIsManual) => valAuthType && valIsManual,
      then: (schem) =>
        yup.string().required().min(3, 'la contrasena debe contener al menos 3 letras'),
    }),
  password: yup
    .string()
    .nullable()
    .when(['authType', 'isManual'], {
      is: (valAuthType, valIsManual) => valAuthType && valIsManual,
      then: (schem) =>
        yup.string().required().min(3, 'la contrasena debe contener al menos 3 letras'),
    }),
});

const defaultValues = {
  name: '',
  email: '',
  domain: '',
  phoneCode: '+52',
  phone: '',
  isManual: false,
  connection: '',
  ip: '',
  owner: '',
  servicePlan: '',
  baseUri: '',
  redisUser: '',
  redisPassword: '',
  authType: false,
  key: '',
  user: '',
  password: '',
};

export const Register = ({ onSuccess = () => {} }) => {
  const { setCompanyId, setCompanyEmail, currentStep, setCompanyName } = useContext(StepperContext);
  const [avatar, setAvatar] = useState(undefined);
  const [pleskConnsList, setPleskConnsList] = useState([]);
  const [typeSelect, setTypeSelect] = useState();

  const Dispatch = useDispatch();

  // ----- Inicializacion de validadores de formulario
  const { control, formState, handleSubmit, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(pleskSchema),
  });
  const { isValid, dirtyFields, errors } = formState;
  const watchIsManual = watch('isManual', false);
  const watchAuthType = watch('authType', false);
  const watchConnectionType = watch('connection');

  // ------ Request Store Company
  const { makeRequest: tryStore, response: responseForm, isLoading } = useRequest(createMainDomain);
  const { makeRequest: tryGetPleskConns, response: responsePleskConns } =
    useRequest(availablePleskConnections);

  const handleLoadToPleskPicker = (objItem) => {
    const convertedObjVersions = convertObjToPickerMono(objItem);
    setPleskConnsList(convertedObjVersions);
  };

  useEffect(() => {
    tryGetPleskConns();
  }, []);

  useEffect(() => {
    if (responsePleskConns === undefined) return;
    handleLoadToPleskPicker(responsePleskConns);
  }, [responsePleskConns]);

  const handleLoadImage = (image) => {
    setAvatar(image);
  };

  // ---- Handler para realizar el registro de la compania en Plesk ---------
  const handleRegisterCompany = (currentForm) => {
    setCompanyName(currentForm.name);
    setCompanyEmail(currentForm.email);
    const { isManual, authType } = currentForm;

    // isEmptyString()

    let formCompany = {
      name: currentForm.name,
      email: currentForm.email,
      phoneCode: currentForm.phoneCode,
      phone: currentForm.phone,
      connection: isManual ? 'manual' : currentForm.connection,
    };
    const isEmptyDomain = isEmptyString(currentForm.domain);
    if (!isEmptyDomain) {
      formCompany = {
        ...formCompany,
        domain: currentForm.domain,
      };
    }
    if (isManual) {
      formCompany = {
        ...formCompany,
        ip: currentForm.ip,
        owner: currentForm.owner,
        servicePlan: currentForm.servicePlan,
        baseUri: currentForm.baseUri,
      };
      if (!authType) {
        formCompany = {
          ...formCompany,
          key: currentForm.key,
        };
      } else {
        formCompany = {
          ...formCompany,
          user: currentForm.user,
          password: currentForm.password,
        };
      }
    }

    console.log(JSON.stringify(formCompany, null, 1));

    tryStore(formCompany).then(({ isOk }) => {
      if (isOk) {
        Dispatch(
          showMessage({
            message: 'Compañia registrada correctamente',
            autoHideDuration: 4000,
            variant: 'success',
          })
        );
      }
    });
  };

  const handleCreate = (form) => {
    handleRegisterCompany(form);
  };

  useEffect(() => {
    console.log(responseForm);
    if (responseForm === undefined || responseForm.id === undefined) return;
    setCompanyId(responseForm.id);
    onSuccess(true);
  }, [responseForm]);

  const handleViewModal = useCallback(() => {
    Dispatch(showConnectionPlesk(watchConnectionType, responsePleskConns));
  }, [watchConnectionType, responsePleskConns]);

  return (
    <>
      <FormCardLayout.FullColum>
        <form
          name="companyCreateForm"
          noValidate
          className="flex flex-col justify-center w-full"
          autoComplete="off"
        >
          <div className="flex flex-col">
            <div className="py-24">
              <Divider>Información de compañía</Divider>
            </div>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className="mb-32"
                  label="Nombre"
                  autoFocus
                  type="text"
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                  variant="outlined"
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className="mb-32"
                  label="Correo electrónico de contacto"
                  autoFocus
                  type="email"
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                  variant="outlined"
                  required
                  fullWidth
                />
              )}
            />
            <Grid className="mb-32" spacing={2.5} container>
              <Grid xs={3} item>
                <Controller
                  name="phoneCode"
                  control={control}
                  render={({ field }) => (
                    <PhoneCodeSelect
                      {...field}
                      type="text"
                      error={!!errors.phoneCode}
                      helperText={errors?.phoneCode?.message}
                      variant="outlined"
                      required
                      fullWidth
                      inputProps={{
                        style: {
                          height: '5.3rem',
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid xs={9} item>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Número telefónico"
                      autoFocus
                      type="number"
                      error={!!errors.phone}
                      helperText={errors?.phone?.message}
                      variant="outlined"
                      fullWidth
                      onChange={(current) => {
                        field.onChange(current);
                      }}
                      InputProps={{
                        inputProps: {
                          style: {
                            WebkitAppearance: 'none',
                            margin: 0,
                            MozAppearance: 'textfield',
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <div className="flex-row w-full flex items-center justify-around">
              <div className="flex gap-10 items-center justify-center">
                <Controller
                  name="connection"
                  control={control}
                  render={({ field }) => (
                    <BasicSelect
                      value={field.value}
                      data={pleskConnsList}
                      title="Conexiones disponibles"
                      error={!!errors.connection}
                      required
                      disabled={watchIsManual}
                      staticWidth="24rem"
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
                <Button
                  onClick={handleViewModal}
                  disabled={!watchConnectionType || watchConnectionType.length === 0}
                  variant="text"
                >
                  <HelpIcon
                    sx={{
                      color:
                        !watchConnectionType || watchConnectionType.length === 0
                          ? 'grey'
                          : 'primary.main',
                    }}
                  />
                </Button>
              </div>
            </div>
            <div className="py-24">
              <Divider>Seccion Plesk</Divider>
            </div>
            <Controller
              name="domain"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className="mb-32"
                  label="Dominio (Opcional)"
                  type="text"
                  error={!!errors.domain}
                  helperText={errors?.domain?.message}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
            {/* <div className="flex-row w-full flex items-center justify-center"> */}
            {/*  <Controller */}
            {/*    name="isManual" */}
            {/*    control={control} */}
            {/*    render={({ field }) => ( */}
            {/*      <div className="flex flex-col justify-center items-center"> */}
            {/*        <FormLabel component="legend">Configuracion manual</FormLabel> */}
            {/*        <FormControlLabel */}
            {/*          control={ */}
            {/*            <Switch */}
            {/*              checked={field.value} */}
            {/*              {...field} */}
            {/*              inputProps={{ 'aria-label': 'Estatus' }} */}
            {/*            /> */}
            {/*          } */}
            {/*          label={field.value ? 'Si' : 'No'} */}
            {/*        /> */}
            {/*      </div> */}
            {/*    )} */}
            {/*  /> */}
            {/* </div> */}
            {watchIsManual && (
              <>
                <div className="py-16">
                  <Divider>Configuracion manual</Divider>
                </div>
                <Controller
                  name="ip"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mb-32"
                      label="Direccion IP"
                      type="text"
                      error={!!errors.ip}
                      helperText={errors?.ip?.message}
                      variant="outlined"
                      fullWidth
                      required={watchIsManual}
                    />
                  )}
                />
                <Controller
                  name="owner"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mb-32"
                      label="Plesk Owner"
                      type="text"
                      error={!!errors.owner}
                      helperText={errors?.owner?.message}
                      variant="outlined"
                      fullWidth
                      required={watchIsManual}
                    />
                  )}
                />
                <Controller
                  name="servicePlan"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mb-32"
                      label="Plan de Servicio Plesk"
                      type="text"
                      error={!!errors.servicePlan}
                      helperText={errors?.servicePlan?.message}
                      variant="outlined"
                      fullWidth
                      required={watchIsManual}
                    />
                  )}
                />
                <Controller
                  name="baseUri"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mb-32"
                      label="URI Base Plesk"
                      type="text"
                      error={!!errors.baseUri}
                      helperText={errors?.baseUri?.message}
                      variant="outlined"
                      fullWidth
                      required={watchIsManual}
                    />
                  )}
                />
                <Controller
                  name="redisUser"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mb-32"
                      label="Redis User"
                      type="text"
                      error={!!errors.redisUser}
                      helperText={errors?.redisUser?.message}
                      variant="outlined"
                      fullWidth
                      required={watchIsManual}
                    />
                  )}
                />
                <Controller
                  name="redisPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mb-32"
                      label="Redis Contraseña"
                      type="text"
                      error={!!errors.redisPassword}
                      helperText={errors?.redisPassword?.message}
                      variant="outlined"
                      fullWidth
                      required={watchIsManual}
                    />
                  )}
                />
                {/** --------------- Switcher ------------------ */}
                <Controller
                  name="authType"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-col justify-center items-center">
                      <FormLabel component="legend">Tipo de autenticacion</FormLabel>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            {...field}
                            inputProps={{ 'aria-label': 'Tipo de autenticacion' }}
                          />
                        }
                        label={field.value ? 'Usuario/Contrasena' : 'Key (Token)'}
                      />
                    </div>
                  )}
                />
                {/** ---------------  Alternatives ------------- */}
                {!watchAuthType ? (
                  <Controller
                    name="key"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Key (Token)"
                        type="text"
                        error={!!errors.key}
                        helperText={errors?.key?.message}
                        variant="outlined"
                        required={!watchAuthType}
                        fullWidth
                      />
                    )}
                  />
                ) : (
                  <>
                    <Controller
                      name="user"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="mb-32"
                          label="Usuario Plesk"
                          type="text"
                          error={!!errors.user}
                          helperText={errors?.user?.message}
                          variant="outlined"
                          required={watchAuthType}
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="mb-32"
                          label="Contrasena Plesk"
                          type="password"
                          error={!!errors.password}
                          helperText={errors?.password?.message}
                          variant="outlined"
                          required={watchAuthType}
                          fullWidth
                        />
                      )}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </form>
      </FormCardLayout.FullColum>
      {currentStep === 0 && (
        <FormCardLayout.RowButton>
          <Button
            onClick={() => history.replace('/companies')}
            variant="contained"
            className="w-full md:w-auto"
            color="error"
          >
            Cancelar
          </Button>
          <ButtonWithLoader
            onClick={handleSubmit(handleCreate)}
            variant="contained"
            aria-label="Crear suscripcion"
            className="w-full md:w-auto"
            color="success"
            type="submit"
            disabled={_.isEmpty(dirtyFields) || !isValid}
            isLoading={isLoading}
          >
            Registrar
          </ButtonWithLoader>
        </FormCardLayout.RowButton>
      )}
    </>
  );
};
